import React, { useEffect, useMemo, useState } from "react";
import { BsPeople } from "react-icons/bs";
import { HiEllipsisVertical } from "react-icons/hi2";
import { LuPenLine, LuUnplug } from "react-icons/lu";
import { toast } from "react-toastify";
import { useDebounce } from "usehooks-ts";

import * as devConfig from "../../app/configs/devConfig";
import { getAuthToken } from "../../app/globalState/GlobalAuthState";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import PreviewFile from "../../components/modal/PreviewFile";
import { SettingsConfirmModal } from "../../components/modal/SettingsConfirmModal";
import { Email } from "../../factories/emails/models/emails.models";
import useSignalR from "../../hooks/useSignalR";
import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetCustomersByEmailQuery } from "../../slices/ContactsSlice";
import {
  useDisconnectEmailMutation,
  useGetEmailFoldersQuery,
  useGetRedirectUrlMutation,
  useLazyGetEmailsQuery,
  useLazyGetLinkedWorkQuery,
  useLazyGetStarredQuery,
  useLazySearchEmailQuery,
  useStarredEmailMutation,
  useUnlinkEmailMutation,
} from "../../slices/EmailsSlice";
import { AddEmailModal } from "../contacts/modals/AddEmailModal";
import { SyncModal } from "../contacts/modals/SyncModal";
import { SaveAttachmentModal } from "../files/SaveAttachmentModal";
import { AddWorkModal } from "../works/modals/AddWork.modal";
import { initialsOf } from "./compose/composeEmail.utils";
import {
  ComposeEmailProps,
  ComposeEmailWindow,
} from "./compose/ComposeEmailWindow";
import { SparkleIcon } from "./compose/SparkleIcon";
import { fetchAttachmentFile } from "./mail/attachmentFile";
import { MailFolderRail } from "./mail/MailFolderRail";
import { ClientLookup, MailFilter, MailList } from "./mail/MailList";
import {
  briefLines,
  buildFolders,
  clientLookupAddresses,
  DEFAULT_FOLDERS,
  inView,
  isClientEmail,
  isInDeleted,
  isOutgoingFolder,
  loadReadingPane,
  MailFolder,
  ReadingPane,
  saveReadingPane,
} from "./mail/mailPage.utils";
import { MailReader } from "./mail/MailReader";
import { ReadingPaneMenu } from "./mail/ReadingPaneMenu";
import { useMailAi } from "./mail/useMailAi";
import { DeleteEmailModal } from "./modals/DeleteEmail";

const NOT_CONNECTED =
  "Grant for this User doesn't exists. Try authorizing again.";

type ComposeState = Omit<ComposeEmailProps, "isActive" | "onClose"> | null;

/**
 * Mail page (V53, Roadmap 1840): mailbox folders | email list with the AI
 * inbox brief | the open email with its AI summary, next steps and quick
 * replies. The reading pane can be Right, Bottom or Off (like Outlook),
 * remembered per person in this browser. Emails come from the person's
 * connected Microsoft mailbox (Nylas).
 */
export const EmailsView: React.FC<unknown> = () => {
  const { user } = useAppSelector((state) => state.appReducer);
  const userId: string = user?.id || "";
  const myEmail: string = user?.nylasToken?.email || "";

  // ----- Folders and paging -----
  const {
    data: rawFolders,
    isLoading: loadingFolders,
    error: folderError,
  } = useGetEmailFoldersQuery({ userId });
  const { main: folders, more: moreFolders } = useMemo(
    () => buildFolders(rawFolders || []),
    [rawFolders],
  );
  const [folder, setFolder] = useState<MailFolder | null>(null);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<MailFilter>("all");
  const [search, setSearch] = useState("");
  const query = useDebounce(search.trim(), 300);
  const [briefHidden, setBriefHidden] = useState(false);
  const [clients, setClients] = useState<Record<string, string>>({});

  const [
    getEmails,
    { data: listData, error: listError, isFetching: loadingList },
  ] = useLazyGetEmailsQuery();
  const [getStarred, { data: starredData, isFetching: loadingStarred }] =
    useLazyGetStarredQuery();
  const [searchEmails, { data: searchData, isFetching: loadingSearch }] =
    useLazySearchEmailQuery();

  const isConnected =
    listError !== NOT_CONNECTED && folderError !== NOT_CONNECTED;
  // No folders = the mailbox can't be reached: show the standard names greyed out.
  const mailboxReady = isConnected && folders.length > 0;

  // ----- Reading pane (Right / Bottom / Off), remembered per person -----
  const [pane, setPaneState] = useState<ReadingPane>(() =>
    loadReadingPane(userId),
  );
  useEffect(() => setPaneState(loadReadingPane(userId)), [userId]);
  const setPane = (next: ReadingPane) => {
    setPaneState(next);
    saveReadingPane(userId, next);
    // "Off" starts from the list.
    if (next === "off") setOpenId(null);
  };

  useEffect(() => {
    if (!folder && folders.length)
      setFolder(folders.find((f) => f.kind === "inbox") || folders[0]);
  }, [folders.length]);

  const load = () => {
    if (!folder) return;
    if (folder.kind === "starred") getStarred({ userId });
    else
      getEmails({ userId, folderId: folder.id, pageNumber: cursors[page - 1] });
  };
  useEffect(load, [folder?.kind, folder?.id, page, userId]);
  useEffect(() => {
    if (query) searchEmails({ userId, query });
  }, [query, userId]);

  useEffect(() => {
    const next = listData?.nextPageValue;
    if (next && cursors.length === page) setCursors((c) => [...c, next]);
  }, [listData?.nextPageValue]);

  // New mail arrives through SignalR: refresh the list.
  const { message } = useSignalR(
    `${devConfig.getServerBaseUrlForEnv()}/signalRHub`,
    getAuthToken().Authorization,
  );
  useEffect(() => {
    if (message) load();
  }, [message]);

  const pickFolder = (f: MailFolder) => {
    setFolder(f);
    setPage(1);
    setCursors([undefined]);
    setOpenId(null);
    setFilter("all");
  };

  // ----- What's shown -----
  const baseList: Email[] = query
    ? searchData?.data || []
    : folder?.kind === "starred"
    ? starredData?.data || []
    : listData?.data || [];
  const outgoing = !query && isOutgoingFolder(folder?.kind);
  const isInbox = !query && folder?.kind === "inbox";

  // ----- AI -----
  const [openId, setOpenId] = useState<string | null>(null);
  const openEmail = baseList.find((e) => e.id === openId) || null;
  const clientSet = useMemo(() => new Set(Object.keys(clients)), [clients]);
  const ai = useMailAi({
    inboxEmails: isInbox ? baseList : [],
    openEmail,
    loaded: baseList,
    clientEmails: clientSet,
    canTag: isInbox,
    canRead: !outgoing,
  });

  const list = baseList
    .filter((e) => filter !== "unread" || e.unread)
    .filter(
      (e) =>
        filter !== "clients" ||
        isClientEmail(e, outgoing, clientSet, [myEmail]),
    );
  // Every address of the emails on screen is looked up once, so the Clients
  // filter works in Sent / Drafts too (recipients, not the sender = me).
  const lookups = clientLookupAddresses(baseList, outgoing, [myEmail]);
  const foundClient = (address: string, name: string) =>
    setClients((c) => (c[address] ? c : { ...c, [address]: name }));
  const brief = isInbox ? briefLines(baseList, ai.tags) : [];
  // The reader shows the open email only while it's in the list on screen.
  const shownEmail =
    openEmail && list.some((e) => e.id === openEmail.id) ? openEmail : null;
  const docCount = isInbox
    ? baseList.filter((e) => inView(ai.tags[e.id], "docs")).length
    : 0;

  // ----- Email actions -----
  const [updateEmail] = useStarredEmailMutation();
  const [getLinkedWork, { data: linkedWorkId }] = useLazyGetLinkedWorkQuery();
  const [unlinkWork] = useUnlinkEmailMutation();
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(
    null,
  );

  useEffect(() => {
    if (openEmail?.thread_id) getLinkedWork({ threadId: openEmail.thread_id });
  }, [openEmail?.id]);

  const open = (email: Email) => {
    setOpenId(email.id);
    if (email.unread)
      updateEmail({
        messageId: email.id,
        userId,
        body: { unread: false, starred: email.starred, folders: email.folders },
      });
  };
  const star = (email: Email) =>
    updateEmail({
      messageId: email.id,
      userId,
      body: {
        starred: !email.starred,
        unread: email.unread,
        folders: email.folders,
      },
    });

  // Documents: "Save to client files" (clients matching the sender).
  const [saving, setSaving] = useState<{ id: string; filename: string } | null>(
    null,
  );
  const senderEmail = openEmail?.from?.[0]?.email || "";
  const { data: senderClients } = useGetCustomersByEmailQuery(
    { email: senderEmail, orgId: getLinkedFirm()?.orgId || "" },
    { skip: !senderEmail || !saving },
  );

  // ----- Windows -----
  const [compose, setCompose] = useState<ComposeState>(null);
  const [toDelete, setToDelete] = useState<{
    id: string;
    permanent: boolean;
  } | null>(null);
  const askDelete = () =>
    openEmail &&
    setToDelete({
      id: openEmail.id,
      permanent: isInDeleted(
        openEmail,
        [...folders, ...moreFolders],
        query ? undefined : folder?.kind,
      ),
    });
  const [addToWork, setAddToWork] = useState(false);
  const [createWork, setCreateWork] = useState(false);
  const [sync, setSync] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnect, { isLoading: disconnecting }] =
    useDisconnectEmailMutation();
  const [connect, { data: connectUrl, isSuccess: connectReady }] =
    useGetRedirectUrlMutation();

  useEffect(() => {
    if (connectReady && connectUrl) window.open(connectUrl, "_blank");
  }, [connectReady]);

  const aiContext = ai.insight
    ? { summary: ai.insight.summary, replies: ai.insight.replies }
    : undefined;
  const reply = (opts?: { text?: string; label?: string; prompt?: string }) =>
    openEmail &&
    setCompose({
      isReply: true,
      email: openEmail,
      selectedMail: openEmail,
      aiContext,
      initialText: opts?.text,
      initialTextLabel: opts?.label,
      initialPrompt: opts?.prompt,
    });

  if (loadingFolders) return <LottieLoading />;

  return (
    <div
      className={
        "flex h-[calc(100vh-60px)] min-h-0 min-w-0 flex-col bg-[#F7F8FA]"
      }>
      <div
        className={
          "flex flex-none flex-wrap items-center gap-[14px] px-7 pb-4 pt-[18px] max-md:px-4"
        }>
        <h1
          className={
            "m-0 text-[22px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
          }>
          Email
        </h1>
        {isConnected && myEmail && (
          <span
            className={
              "inline-flex h-8 items-center gap-2 rounded-[20px] border border-[#E5E7EB] bg-white pl-1 pr-[13px]"
            }>
            <span
              className={
                "inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#16A34A] text-[10px] font-bold text-white"
              }>
              {initialsOf(
                `${user?.userProfile?.firstName || ""} ${
                  user?.userProfile?.lastName || ""
                }`.trim() || myEmail,
              )}
            </span>
            <span className={"text-[13px] font-semibold text-[#3A4256]"}>
              {myEmail}
            </span>
            <span
              title={"Connected"}
              className={"h-[7px] w-[7px] rounded-full bg-[#2FC66E]"}
            />
          </span>
        )}
        <span className={"flex-1"} />
        {mailboxReady && <ReadingPaneMenu value={pane} onChange={setPane} />}
        {isConnected && (
          <>
            {ai.isOn && (
              <button
                type={"button"}
                onClick={() => setCompose({ initialPrompt: undefined })}
                className={
                  "inline-flex h-10 items-center gap-2 rounded-[9px] border-[1.5px] border-[#DCD3FF] bg-white px-4 text-[14px] font-bold text-[#5B3FE0] hover:border-[#B9A9FF] hover:bg-[#F6F3FF]"
                }>
                <SparkleIcon size={16} />
                Write with AI
              </button>
            )}
            <button
              type={"button"}
              onClick={() => setCompose({})}
              className={
                "inline-flex h-10 items-center gap-2 rounded-[9px] bg-[#6B4EFF] px-[18px] text-[14px] font-bold text-white shadow-[0_3px_10px_rgba(107,78,255,0.25)] hover:bg-[#5D4DB4]"
              }>
              <LuPenLine size={16} />
              Compose
            </button>
            <span className={"relative"}>
              <button
                type={"button"}
                aria-label={"More"}
                onClick={() => setMenuOpen((v) => !v)}
                className={
                  "inline-flex h-10 w-10 items-center justify-center rounded-[9px] text-[#5B6678] hover:bg-white"
                }>
                <HiEllipsisVertical size={22} />
              </button>
              {menuOpen && (
                <>
                  <span
                    className={"fixed inset-0 z-30"}
                    onClick={() => setMenuOpen(false)}
                  />
                  <span
                    className={
                      "absolute right-0 top-[calc(100%+6px)] z-40 flex w-[260px] flex-col rounded-[11px] border border-[#ECEEF2] bg-white p-[6px] shadow-[0_14px_40px_rgba(20,22,34,0.22)]"
                    }>
                    <button
                      type={"button"}
                      onClick={() => {
                        setMenuOpen(false);
                        setSync(true);
                      }}
                      className={
                        "flex items-center gap-[10px] rounded-[8px] px-[11px] py-[9px] text-left text-[13.5px] font-semibold text-[#3A4256] hover:bg-[#F4F2FF]"
                      }>
                      <BsPeople size={16} />
                      Sync emails with clients/works
                    </button>
                    <button
                      type={"button"}
                      onClick={() => {
                        setMenuOpen(false);
                        setDisconnectOpen(true);
                      }}
                      className={
                        "flex items-center gap-[10px] rounded-[8px] px-[11px] py-[9px] text-left text-[13.5px] font-semibold text-[#E5484D] hover:bg-[#FEECEC]"
                      }>
                      <LuUnplug size={16} />
                      Disconnect my email
                    </button>
                  </span>
                </>
              )}
            </span>
          </>
        )}
      </div>

      <div
        className={
          "flex min-h-0 flex-1 gap-4 px-7 pb-6 max-md:flex-col max-md:gap-3 max-md:px-4"
        }>
        <MailFolderRail
          folders={mailboxReady ? folders : DEFAULT_FOLDERS}
          moreFolders={mailboxReady ? moreFolders : []}
          current={query ? null : folder}
          onPick={(f) => {
            setSearch("");
            pickFolder(f);
          }}
          disabled={!mailboxReady}
        />
        {!mailboxReady ? (
          <div
            className={
              "flex min-w-0 flex-1 flex-col items-center justify-center gap-3 rounded-[12px] border border-[#E5E7EB] bg-white p-6 pb-24 text-center"
            }>
            <span
              className={
                "inline-flex h-14 w-14 items-center justify-center rounded-[14px] bg-[#F1EEFF] text-[#6B4EFF]"
              }>
              <LuPenLine size={24} />
            </span>
            <span className={"text-[16px] font-bold text-[#1F2433]"}>
              {isConnected
                ? "Your mailbox isn't available"
                : "Connect your mailbox"}
            </span>
            <span className={"max-w-[380px] text-[13.5px] text-[#6B7185]"}>
              {isConnected
                ? "We couldn't load your mailbox folders. Reconnect your Microsoft mailbox to read and send email here."
                : "Connect your Microsoft mailbox to read and send email here, and link emails to clients and work."}
            </span>
            <button
              type={"button"}
              onClick={() =>
                connect({
                  provider: "microsoft",
                  returnUrl: document.URL,
                  userId,
                })
              }
              className={
                "mt-1 h-10 rounded-[9px] bg-[#6B4EFF] px-5 text-[14px] font-bold text-white"
              }>
              {isConnected ? "Reconnect" : "Connect"}
            </button>
          </div>
        ) : pane === "off" && shownEmail ? (
          <MailReader
            email={shownEmail}
            outgoing={outgoing}
            linkedWorkId={openEmail && linkedWorkId ? linkedWorkId : undefined}
            tag={openEmail ? ai.tags[openEmail.id] : undefined}
            aiOn={ai.isOn}
            insight={ai.insight}
            reading={ai.reading}
            readError={ai.readError}
            autoRead={ai.autoRead}
            onRead={(force) => openEmail && ai.read(openEmail, force)}
            onReply={reply}
            onReplyAll={() =>
              openEmail &&
              setCompose({
                isReplyAll: true,
                email: openEmail,
                selectedMail: openEmail,
                aiContext,
              })
            }
            onForward={() =>
              openEmail && setCompose({ isForward: true, email: openEmail })
            }
            onStar={() => openEmail && star(openEmail)}
            onDelete={askDelete}
            onAddToWork={() => setAddToWork(true)}
            onCreateWork={() => setCreateWork(true)}
            onUnlinkWork={() =>
              openEmail &&
              linkedWorkId &&
              unlinkWork({
                body: {
                  messageId: openEmail.id,
                  threadId: openEmail.thread_id || "",
                  workId: linkedWorkId,
                },
              })
                .unwrap()
                .then(() => {
                  toast.success("Email unlinked from the work item");
                  if (openEmail.thread_id)
                    getLinkedWork({ threadId: openEmail.thread_id });
                })
                .catch(() =>
                  toast.error("Couldn't unlink the email. Please try again."),
                )
            }
            onOpenAttachment={(f) => {
              if (!openEmail) return;
              setPreview({ name: f.filename, url: "" });
              fetchAttachmentFile({
                userId,
                messageId: openEmail.id,
                attachmentId: f.id || "",
                fileName: f.filename,
              })
                .then((url) =>
                  setPreview((cur) =>
                    cur && cur.name === f.filename ? { ...cur, url } : cur,
                  ),
                )
                .catch(() => {
                  setPreview(null);
                  toast.error(
                    "Couldn't open the attachment. Please try again.",
                  );
                });
            }}
            onSaveAttachment={(f) =>
              setSaving({ id: f.id || "", filename: f.filename })
            }
            onCompose={() => setCompose({})}
            onBack={pane === "off" ? () => setOpenId(null) : undefined}
            backLabel={`Back to ${
              query ? "search results" : folder?.label || "the list"
            }`}
          />
        ) : (
          <div
            className={`flex min-h-0 min-w-0 flex-1 gap-4 ${
              pane === "bottom" ? "flex-col" : ""
            }`}>
            <MailList
              emails={list}
              openId={openId}
              onOpen={open}
              onStar={star}
              outgoing={outgoing}
              tags={ai.tags}
              search={search}
              setSearch={setSearch}
              searchPlaceholder={
                query
                  ? "Search all mail"
                  : `Search ${(folder?.label || "mail").toLowerCase()}`
              }
              filter={filter}
              setFilter={setFilter}
              brief={brief}
              briefExtra={docCount ? `${docCount} with documents` : ""}
              showBrief={ai.isOn && isInbox && filter === "all" && !briefHidden}
              onHideBrief={() => setBriefHidden(true)}
              pane={pane}
              onClientFound={foundClient}
              loading={
                (query ? loadingSearch : loadingList || loadingStarred) &&
                baseList.length === 0
              }
              emptyTitle={
                query
                  ? "No emails match your search"
                  : `Nothing in ${folder?.label || "this folder"}`
              }
              emptyText={
                query
                  ? "Try a different name or keyword."
                  : filter !== "all"
                  ? "Try switching the filter to All."
                  : "Emails will appear here."
              }
              page={page}
              hasNext={
                !query && folder?.kind !== "starred" && cursors.length > page
              }
              onPrev={() => setPage((n) => Math.max(1, n - 1))}
              onNext={() => setPage((n) => n + 1)}
            />
            {pane !== "off" && (
              <MailReader
                email={shownEmail}
                outgoing={outgoing}
                linkedWorkId={
                  openEmail && linkedWorkId ? linkedWorkId : undefined
                }
                tag={openEmail ? ai.tags[openEmail.id] : undefined}
                aiOn={ai.isOn}
                insight={ai.insight}
                reading={ai.reading}
                readError={ai.readError}
                autoRead={ai.autoRead}
                onRead={(force) => openEmail && ai.read(openEmail, force)}
                onReply={reply}
                onReplyAll={() =>
                  openEmail &&
                  setCompose({
                    isReplyAll: true,
                    email: openEmail,
                    selectedMail: openEmail,
                    aiContext,
                  })
                }
                onForward={() =>
                  openEmail && setCompose({ isForward: true, email: openEmail })
                }
                onStar={() => openEmail && star(openEmail)}
                onDelete={askDelete}
                onAddToWork={() => setAddToWork(true)}
                onCreateWork={() => setCreateWork(true)}
                onUnlinkWork={() =>
                  openEmail &&
                  linkedWorkId &&
                  unlinkWork({
                    body: {
                      messageId: openEmail.id,
                      threadId: openEmail.thread_id || "",
                      workId: linkedWorkId,
                    },
                  })
                    .unwrap()
                    .then(() => {
                      toast.success("Email unlinked from the work item");
                      if (openEmail.thread_id)
                        getLinkedWork({ threadId: openEmail.thread_id });
                    })
                    .catch(() =>
                      toast.error(
                        "Couldn't unlink the email. Please try again.",
                      ),
                    )
                }
                onOpenAttachment={(f) => {
                  if (!openEmail) return;
                  setPreview({ name: f.filename, url: "" });
                  fetchAttachmentFile({
                    userId,
                    messageId: openEmail.id,
                    attachmentId: f.id || "",
                    fileName: f.filename,
                  })
                    .then((url) =>
                      setPreview((cur) =>
                        cur && cur.name === f.filename ? { ...cur, url } : cur,
                      ),
                    )
                    .catch(() => {
                      setPreview(null);
                      toast.error(
                        "Couldn't open the attachment. Please try again.",
                      );
                    });
                }}
                onSaveAttachment={(f) =>
                  setSaving({ id: f.id || "", filename: f.filename })
                }
                onCompose={() => setCompose({})}
                onBack={undefined}
                backLabel={`Back to ${
                  query ? "search results" : folder?.label || "the list"
                }`}
              />
            )}
          </div>
        )}
      </div>

      {compose && (
        <ComposeEmailWindow
          isActive
          onClose={() => setCompose(null)}
          {...compose}
        />
      )}
      {toDelete && (
        <DeleteEmailModal
          isActive
          emailId={toDelete.id}
          permanent={toDelete.permanent}
          onClose={() => setToDelete(null)}
          setSelectedMail={() => setOpenId(null)}
        />
      )}
      {lookups.map((address) => (
        <ClientLookup key={address} address={address} onFound={foundClient} />
      ))}
      {addToWork && openEmail && (
        <AddEmailModal
          isActive
          onClose={() => setAddToWork(false)}
          contactId={userId}
          selectedMail={openEmail}
        />
      )}
      {createWork && openEmail && (
        <AddWorkModal
          isOpen
          onClose={() => setCreateWork(false)}
          selectedMail={openEmail}
        />
      )}
      {saving && openEmail && (
        <SaveAttachmentModal
          clients={(senderClients || []).map((c) => ({
            id: c.id,
            name: c.name,
          }))}
          messageId={openEmail.id}
          attachmentId={saving.id}
          fileName={saving.filename}
          onClose={() => setSaving(null)}
          onSaved={(message) => {
            setSaving(null);
            toast.success(message);
          }}
        />
      )}
      {sync && <SyncModal isActive onClose={() => setSync(false)} />}
      {preview && (
        <PreviewFile
          close={() => {
            if (preview.url) URL.revokeObjectURL(preview.url);
            setPreview(null);
          }}
          file={preview.url}
          fileName={preview.name}
        />
      )}
      {disconnectOpen && (
        <SettingsConfirmModal
          onClose={() => setDisconnectOpen(false)}
          onConfirm={async () => {
            try {
              await disconnect({ userId }).unwrap();
              setDisconnectOpen(false);
            } catch {
              toast.error("Couldn't disconnect your email. Please try again.");
            }
          }}
          isLoading={disconnecting}
          icon={<LuUnplug size={20} />}
          title={"Disconnect Email?"}
          description={
            "Are you sure you want to disconnect your email?<br />You will no longer be able to send or receive emails here. You can reconnect at any time."
          }
          confirmText={"Disconnect"}
          variant={"danger"}
        />
      )}
    </div>
  );
};
